import React from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { scaleBand } from 'd3-scale';
import { DateRange } from '../filters/dateRange';
import { ConvertedTokensDay } from './usageBreakdown';
import { GRID_STROKE, MUTED_INK, X_AXIS_PROPS, Y_AXIS_PROPS, ChartTooltip, formatCompact } from './chartKit';

// Recharts accepts D3 scales, but their type overloads differ.
const BAND_SCALE = scaleBand() as React.ComponentProps<typeof XAxis>['scale'];
const SERIES = [
  { dataKey: 'metered', label: 'Metered', color: '#199e70' },
  { dataKey: 'duplicate', label: 'Duplicate', color: '#c98500' },
] as const;

interface Props {
  rows: ConvertedTokensDay[];
  range: DateRange | null;
  error?: boolean;
}

export default function ConvertedTokensChart({ rows, range, error }: Props) {
  const totals = {
    metered: rows.reduce((sum, row) => sum + row.metered, 0),
    duplicate: rows.reduce((sum, row) => sum + row.duplicate, 0),
  };
  const total = totals.metered + totals.duplicate;
  const series = [...SERIES].sort((a, b) => totals[b.dataKey] - totals[a.dataKey]);

  return (
    <div className="bg-widget-bg rounded-lg border border-gray-700 p-4 mb-6">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <span className="text-sm text-heading">Converted Input Tokens by Billing Status</span>
        <div className="ml-auto text-sm text-secondary">
          Total converted input tokens: <span className="text-heading font-medium">{error ? '—' : total.toLocaleString()}</span>
        </div>
      </div>
      {!range ? (
        <p className="text-sm text-secondary py-8 text-center">Pick a valid date range.</p>
      ) : error ? (
        <p className="text-sm text-red-400 py-8 text-center">Failed to load the chart.</p>
      ) : total === 0 ? (
        <p className="text-sm text-secondary py-8 text-center">No usage in this period.</p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          {/* Recharts stacks in mount order, so the chart remounts when the series order changes. */}
          <AreaChart key={series.map((s) => s.dataKey).join()} data={rows}>
            <CartesianGrid vertical={false} stroke={GRID_STROKE} />
            {/* A D3 band scale: recharts 3.1 misplaces ticks with scale="band". */}
            <XAxis dataKey="label" scale={BAND_SCALE} {...X_AXIS_PROPS} />
            <YAxis {...Y_AXIS_PROPS} />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: MUTED_INK }} />
            {[...series].reverse().map((s) => (
              <Area key={s.dataKey} dataKey={s.dataKey} name={s.label} type="monotone" stackId="converted" fill={s.color} fillOpacity={0.9} stroke={s.color} isAnimationActive={false} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 px-1">
        {series.map((s) => (
          <span key={s.dataKey} className="flex items-center gap-1.5 text-xs text-body-color">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
            {s.label}{!error && ` (${formatCompact(totals[s.dataKey])})`}
          </span>
        ))}
      </div>
    </div>
  );
}
